import { API_CONFIG, buildApiUrl, getAuthHeaders } from '../config/api.ts';
import { apiRequest } from '../utils/apiRequest.ts';

// Types for Reservation Forms API
export interface ReservationForm {
  id: number;
  contactName: string;
  phone: string | null;
  email: string | null;
  reservationDate: string;
  arrivalTime: string;
  guests: string | null; // JSON string
  tablePreference: string | null; // JSON string array
  occasion: string | null;
  allergies: string | null;
  specialRequests: string | null;
  status: string | null;
  assignedTable: string | null;
  staffInitials: string | null;
  createdDate: string;
}

export interface CreateReservationFormRequest {
  contactName: string;
  phone?: string | null;
  email?: string | null;
  reservationDate: string;
  arrivalTime: string;
  guests?: string | null;
  tablePreference?: string | null;
  occasion?: string | null;
  allergies?: string | null;
  specialRequests?: string | null;
  status?: string | null;
  assignedTable?: string | null;
  staffInitials?: string | null;
}

export interface UpdateReservationFormRequest {
  contactName?: string;
  phone?: string | null;
  email?: string | null;
  reservationDate?: string;
  arrivalTime?: string;
  guests?: string | null;
  tablePreference?: string | null;
  occasion?: string | null;
  allergies?: string | null;
  specialRequests?: string | null;
  status?: string | null;
  assignedTable?: string | null;
  staffInitials?: string | null;
}

export interface PaginatedReservationFormsResponse {
  data: ReservationForm[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  contactName?: string;
  phone?: string;
  email?: string;
  reservationDate?: string;
  createdDate?: string;
}

// Reservation Forms API calls
export const reservationFormsApi = {
  // Get all reservation forms with pagination and filtering
  getReservationForms: async (
    params: PaginationParams = {},
    token: string
  ): Promise<PaginatedReservationFormsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.contactName) searchParams.append('contactName', params.contactName);
    if (params.phone) searchParams.append('phone', params.phone);
    if (params.email) searchParams.append('email', params.email);
    if (params.reservationDate) searchParams.append('reservationDate', params.reservationDate);
    if (params.createdDate) searchParams.append('createdDate', params.createdDate);

    const url = `${buildApiUrl(API_CONFIG.ENDPOINTS.RESERVATION_FORMS.BASE)}?${searchParams.toString()}`;
    
    return apiRequest<PaginatedReservationFormsResponse>(
      url,
      {
        method: 'GET',
        headers: getAuthHeaders(token),
      }
    );
  },

  // Get reservation form by ID
  getReservationForm: async (id: number, token: string): Promise<ReservationForm> => {
    return apiRequest<ReservationForm>(
      buildApiUrl(API_CONFIG.ENDPOINTS.RESERVATION_FORMS.BY_ID(id)),
      {
        method: 'GET',
        headers: getAuthHeaders(token),
      }
    );
  },

  // Create new reservation form
  createReservationForm: async (data: CreateReservationFormRequest, token: string): Promise<ReservationForm> => {
    return apiRequest<ReservationForm>(
      buildApiUrl(API_CONFIG.ENDPOINTS.RESERVATION_FORMS.BASE),
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data),
      }
    );
  },

  // Update reservation form
  updateReservationForm: async (
    id: number,
    data: UpdateReservationFormRequest,
    token: string
  ): Promise<ReservationForm> => {
    return apiRequest<ReservationForm>(
      buildApiUrl(API_CONFIG.ENDPOINTS.RESERVATION_FORMS.BY_ID(id)),
      {
        method: 'PATCH',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data),
      }
    );
  },

  // Delete reservation form
  deleteReservationForm: async (id: number, token: string): Promise<void> => {
    return apiRequest<void>(
      buildApiUrl(API_CONFIG.ENDPOINTS.RESERVATION_FORMS.BY_ID(id)),
      {
        method: 'DELETE',
        headers: getAuthHeaders(token),
      }
    );
  },
};

